import type { Path } from '$app/types'
import Bot from '@lucide/svelte/icons/bot'
import Box from '@lucide/svelte/icons/box'
import BrainCircuit from '@lucide/svelte/icons/brain-circuit'
import ChartNoAxesColumn from '@lucide/svelte/icons/chart-no-axes-column'
import CircleDollarSign from '@lucide/svelte/icons/circle-dollar-sign'
import CreditCard from '@lucide/svelte/icons/credit-card'
import CreditCardReader from '@lucide/svelte/icons/credit-card-reader'
import Gauge from '@lucide/svelte/icons/gauge'
import HandCoins from '@lucide/svelte/icons/hand-coins'
import Heart from '@lucide/svelte/icons/heart'
import Mail from '@lucide/svelte/icons/mail'
import Megaphone from '@lucide/svelte/icons/megaphone'
import Network from '@lucide/svelte/icons/network'
import Send from '@lucide/svelte/icons/send'
import SquareTerminal from '@lucide/svelte/icons/square-terminal'
import SquareText from '@lucide/svelte/icons/square-text'
import StarPlus from '@lucide/svelte/icons/star-plus'
import SwatchBook from '@lucide/svelte/icons/swatch-book'
import Timer from '@lucide/svelte/icons/timer'
import Workflow from '@lucide/svelte/icons/workflow'
import type { Component } from 'svelte'

import { m } from '#lib/paraglide/messages.js'
import { updateGetListedDialog } from '#lib/utils/dialogs.svelte.js'
import { phg } from '#lib/utils/posthog.js'
import Cmc from '~icons/simple-icons/coinmarketcap'
import LinkedIn from '~icons/simple-icons/linkedin'
import Twitter from '~icons/simple-icons/x'

import { CHAINS_LIST, type ChainConfig } from './chainsConfig'

// menu items never use param routes, so list them explicitly to keep resolve() overload-safe
type StaticPathname = Extract<
	Path,
	| 'dashboard'
	| 'dashboard/financials'
	| 'dashboard/decentralization'
	| 'dashboard/developer-activity'
	| 'dashboard/crypto-card-activity'
	| 'dashboard/crypto-cards'
	| 'dashboard/agent-activity'
	| 'dashboard/agents'
	| 'chart'
	| 'chart/time'
	| 'compare'
	| 'compare/market-cap'
	| 'blog'
	| 'about'
>

type MenuItemInternal = {
	route: StaticPathname
	Icon: Component
	text: string
	description?: string
	new?: boolean
}

type MenuItemExternal = {
	href: string
	Icon: Component
	text: string
	event: string
	new?: boolean
}

type MenuSectionInternal = {
	id: 'dashboards' | 'cards' | 'agents' | 'charts' | 'compare' | 'other'
	text: string
	items: MenuItemInternal[]
	new?: boolean
}

type MenuSectionExternal = {
	id: 'community' | 'contacts'
	text: string
	items: MenuItemExternal[]
	new?: boolean
}

type MenuSectionChains = {
	id: 'chains'
	text: string
	Icon: Component
	chains: ChainConfig[]
	new?: boolean
}

type MenuMapping = {
	dashboards: MenuSectionInternal
	cards: MenuSectionInternal
	agents: MenuSectionInternal
	charts: MenuSectionInternal
	compare: MenuSectionInternal
	chains: MenuSectionChains
	other: MenuSectionInternal
	community: MenuSectionExternal
	contacts: MenuSectionExternal
}

export const MENU_SECTIONS_MAPPING: MenuMapping = {
	dashboards: {
		id: 'dashboards',
		text: 'Blockchains',
		items: [
			{
				route: 'dashboard',
				Icon: Gauge,
				text: m.menu_dashboard_scalability(),
				description:
					'Look beyond marketing claims with real-time TPS, transaction volume, block time, finality, and more.',
			},
			{
				route: 'dashboard/financials',
				Icon: HandCoins,
				text: m.menu_dashboard_financials(),
				description:
					'See which blockchains generate on-chain revenue and how they stack up across fees, market cap, and FDV.',
			},
			{
				route: 'dashboard/decentralization',
				Icon: Network,
				text: m.menu_dashboard_decentralization(),
				description:
					'Find the most decentralized blockchains by Nakamoto Coefficient, validator count, and stake distribution.',
			},
			{
				route: 'dashboard/developer-activity',
				Icon: SquareTerminal,
				text: m.menu_dashboard_dev_activity(),
				description:
					'Discover which blockchain ecosystems are truly alive based on active developers across core repositories.',
			},
		],
	},
	cards: {
		id: 'cards',
		text: 'Crypto Cards',
		new: true,
		items: [
			{
				route: 'dashboard/crypto-card-activity',
				Icon: CreditCardReader,
				text: 'Card Activity',
				description:
					'See which crypto cards people actually use based on transaction volume, number of transactions, and users.',
			},
			{
				route: 'dashboard/crypto-cards',
				Icon: CreditCard,
				text: 'Card Directory',
				description:
					'Find the crypto card that fits how you spend and borrow by comparing annual fees, cashback rates, and more.',
			},
		],
	},
	agents: {
		id: 'agents',
		text: 'AI Agents',
		items: [
			{
				route: 'dashboard/agent-activity',
				Icon: BrainCircuit,
				text: 'Agent Activity',
				description:
					'Cut through the AI hype and see where agents are actually active, transacting, and delivering services on-chain.',
			},
			{
				route: 'dashboard/agents',
				Icon: Bot,
				text: 'Agent Directory',
				description:
					'Meet the AI agents shaping Web3 and discover their capabilities, services, and on-chain presence.',
			},
		],
	},
	charts: {
		id: 'charts',
		text: 'Charts',
		items: [
			{
				route: 'chart',
				Icon: ChartNoAxesColumn,
				text: 'Bar Chart',
			},
			{
				route: 'chart/time',
				Icon: Timer,
				text: 'Time Chart',
			},
		],
	},
	compare: {
		id: 'compare',
		text: 'Compare',
		items: [
			{
				route: 'compare/market-cap',
				Icon: CircleDollarSign,
				text: 'Market Cap',
			},
			{
				route: 'compare',
				Icon: Box,
				text: 'Blockchains',
			},
		],
	},
	chains: { id: 'chains', text: 'Networks', Icon: Workflow, chains: CHAINS_LIST },
	other: {
		id: 'other',
		text: 'Resources',
		items: [
			{
				route: 'blog',
				Icon: SquareText,
				text: 'Blog',
			},
			{
				route: 'about',
				Icon: Heart,
				text: 'About Us',
			},
		],
	},
	community: {
		id: 'community',
		text: 'Community',
		items: [
			{
				href: 'https://x.com/chainspect_app',
				Icon: Twitter,
				text: 'Twitter',
				event: 'twitter_link_click',
			},
			{
				href: 'https://www.linkedin.com/company/107397610',
				Icon: LinkedIn,
				text: 'LinkedIn',
				event: 'linkedin_link_click',
			},
			{
				href: 'https://coinmarketcap.com/community/profile/chainspect_app',
				Icon: Cmc,
				text: 'CoinMarketCap',
				event: 'cmc_link_click',
			},
		],
	},
	contacts: {
		id: 'contacts',
		text: 'Contacts',
		items: [
			{
				href: 'mailto:contact@chainspect.app',
				Icon: Mail,
				text: 'Email',
				event: 'email_link_click',
			},
			{
				href: 'https://t.me/chainspect_app',
				Icon: Send,
				text: 'Telegram',
				event: 'tg_link_click',
			},
			{
				href: 'https://drive.google.com/drive/folders/1ytNeOtXPYQ_fXSyTmIXrEnurzzjiRC06?usp=drive_link',
				Icon: SwatchBook,
				text: 'Media Kit',
				event: 'media_kit_link_click',
			},
			{
				href: 'https://tally.so/r/44qM7k',
				Icon: Megaphone,
				text: m.menu_advertise(),
				event: 'advertise_link_click',
				new: true,
			},
			{
				href: 'https://tally.so/r/n058X6',
				Icon: StarPlus,
				text: m.menu_get_listed(),
				event: 'get_listed_dialog_open',
			},
		],
	},
}

export const MENU_SECTIONS = [
	MENU_SECTIONS_MAPPING.dashboards,
	MENU_SECTIONS_MAPPING.cards,
	MENU_SECTIONS_MAPPING.agents,
	MENU_SECTIONS_MAPPING.charts,
	MENU_SECTIONS_MAPPING.compare,
	MENU_SECTIONS_MAPPING.chains,
	MENU_SECTIONS_MAPPING.other,
	MENU_SECTIONS_MAPPING.community,
	MENU_SECTIONS_MAPPING.contacts,
]

export const isMenuInternal = (
	item: MenuItemInternal | MenuItemExternal
): item is MenuItemInternal => !!(item as MenuItemInternal).route

export const isSectionRegular = (
	section: MenuSectionInternal | MenuSectionExternal | MenuSectionChains
): section is MenuSectionInternal | MenuSectionExternal => !(section as MenuSectionChains).chains

export const handleMenuClick = (
	e: MouseEvent,
	section: MenuSectionInternal | MenuSectionExternal | MenuSectionChains,
	item: MenuItemInternal | MenuItemExternal,
	pathname: string,
	place: string
) => {
	if (section.text === MENU_SECTIONS_MAPPING.dashboards.text && pathname === '/')
		phg.capture('open_dashboard', { place })
	if (!isMenuInternal(item)) phg.capture(item.event, { place })

	if (item.text === m.menu_get_listed()) {
		e.preventDefault()

		updateGetListedDialog({ open: true })
	}
}
